import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

/**
 * The last line of defence against a blank screen.
 *
 * §15 rejects rough edges, and an unhandled render error producing a white page
 * with a console trace is the roughest edge there is. This catches it and shows
 * something a user in a branch can act on.
 *
 * A class component because React still offers no hook equivalent of
 * `componentDidCatch`.
 *
 * Deliberately not translated through i18next: this boundary also catches errors
 * thrown *during* i18n initialisation, and calling `t()` here could throw a
 * second time inside the handler for the first. Both languages are written out
 * literally instead, which is the one place in the app where that is the right
 * call.
 */
interface State {
    error: Error | null;
}

export class AppErrorBoundary extends Component<
    { children: ReactNode },
    State
> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        console.error(
            '[DB-CX] Unhandled render error',
            error,
            info.componentStack,
        );
    }

    private handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        const { error } = this.state;

        if (!error) {
            return this.props.children;
        }

        return (
            <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
                <div className="w-full max-w-md text-center">
                    <div className="mx-auto mb-5 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                        <svg
                            className="size-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                        >
                            <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                        </svg>
                    </div>

                    <h1 className="text-lg font-semibold">
                        Something went wrong
                    </h1>
                    <p lang="ar" dir="rtl" className="text-lg font-semibold">
                        حدث خطأ غير متوقع
                    </p>

                    <p className="mt-3 text-sm text-muted-foreground">
                        The page could not be displayed. Reloading usually
                        resolves it.
                    </p>
                    <p
                        lang="ar"
                        dir="rtl"
                        className="mt-1 text-sm text-muted-foreground"
                    >
                        تعذّر عرض الصفحة. عادةً ما تُحلّ المشكلة بإعادة التحميل.
                    </p>

                    <button
                        type="button"
                        onClick={this.handleReload}
                        className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    >
                        Reload · إعادة التحميل
                    </button>

                    {/*
                     * The message is shown only in development. In production it
                     * could leak internal detail to whoever is standing at a
                     * branch counter (§9.2), and it would mean nothing to them.
                     */}
                    {import.meta.env.DEV && (
                        <pre
                            dir="ltr"
                            className="mt-6 max-h-48 overflow-auto rounded bg-muted p-3 text-start font-mono text-xs text-muted-foreground"
                        >
                            {error.message}
                            {'\n'}
                            {error.stack}
                        </pre>
                    )}
                </div>
            </div>
        );
    }
}
